import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Truck, 
  MapPin, 
  Plus, 
  Trash2, 
  Save, 
  Share2, 
  DollarSign, 
  Clock, 
  Calendar, 
  User, 
  ArrowUpDown,
  Search,
  Filter,
  X,
  Edit,
  CheckSquare
} from 'lucide-react';
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer, InfoWindow } from '@react-google-maps/api';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import { formatArgentinaDate } from '../utils/dateUtils';
import { useToast } from '../hooks/useToast';

// Define types
interface Stop {
  id: string;
  address: string;
  lat: number;
  lng: number;
  name?: string;
  notes?: string;
}

interface Driver {
  id: string;
  name: string;
}

interface Trip {
  id: string;
  name: string;
  driver_id: string;
  date: string;
  cost_per_km: number;
  total_distance: number;
  total_cost: number;
  stops: Stop[];
  status: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
}


// Google Maps libraries to load
const libraries: ("places" | "drawing" | "geometry" | "localContext" | "visualization")[] = ['places', 'geometry'];

const Logistics: React.FC = () => {
  const { toast, ToastComponent } = useToast();
  const { setError } = useAppStore();
  
  // Google Maps API loading
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
  
  // State for map
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [center, setCenter] = useState({ lat: -34.603722, lng: -58.381592 }); // Buenos Aires
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<Stop | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [estimatedTime, setEstimatedTime] = useState<number>(0); // en minutos
  const [trafficCondition, setTrafficCondition] = useState<string>('tráfico normal');
  
  // State for trip planning
  const [stops, setStops] = useState<Stop[]>([]);
  const [newStop, setNewStop] = useState('');
  const [costPerKm, setCostPerKm] = useState<number>(900);
  const [tripName, setTripName] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [tripDate, setTripDate] = useState(new Date().toISOString().split('T')[0]);
  
  // State for trip management
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // State for drivers
  const [drivers, setDrivers] = useState<Driver[]>([
    { id: '1', name: 'Andy' },
    { id: '2', name: 'Gaston' },
    { id: '3', name: 'Juan Cruz' }
  ]);
  
  // State for filters
  const [driverFilter, setDriverFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  
  // Refs
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);

  const [shortenedUrls, setShortenedUrls] = useState<{[key: string]: string}>({});
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false);
  
  // Load trips from database
  useEffect(() => {
    const fetchTrips = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('logistics_trips')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        
        // Parse stops JSON for each trip
        const parsedTrips = data?.map(trip => ({
          ...trip,
          stops: typeof trip.stops === 'string' ? JSON.parse(trip.stops) : trip.stops
        })) || [];
        
        setTrips(parsedTrips);
      } catch (error: any) {
        console.error('Error fetching trips:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchTrips();
  }, [setError]);
  
  // Initialize autocomplete when Google Maps API is loaded
// Initialize autocomplete when Google Maps API is loaded
useEffect(() => {
  if (!isLoaded || !inputRef.current) return;
  
  const options = {
    componentRestrictions: { country: 'ar' }, // Restrict to Argentina
    fields: ['address_components', 'geometry', 'name', 'formatted_address'],
  };
  
  autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, options);
  
  // Solo inicializar si el mapa ya está cargado
  if (map) {
    placesServiceRef.current = new google.maps.places.PlacesService(map);
  }
    
autocompleteRef.current.addListener('place_changed', () => {
  const place = autocompleteRef.current?.getPlace();
  
  if (place && place.geometry && place.geometry.location) {
    setAddressError(null);
    const newStopObj: Stop = {
      id: Date.now().toString(),
      address: place.formatted_address || '',
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      name: place.name || '',
    };
    
    setStops(prev => [...prev, newStopObj]);
    setNewStop('');
    
    // Limpiar URL si se está editando un viaje
    if (isEditing && selectedTrip) {
      setShortenedUrls(prev => {
        const updated = { ...prev };
        delete updated[selectedTrip.id];
        return updated;
      });
    }
    
    // Update map center to the new stop
    setCenter({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng()
    });
  }
});
    
    return () => {
      google.maps.event.clearInstanceListeners(autocompleteRef.current!);
    };
  }, [isLoaded, map]);
  
  // Handle Enter key press in the address input
  const handleAddressKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (!newStop.trim()) {
        setAddressError('Ingrese una dirección');
        return;
      }
      
      if (!isLoaded || !placesServiceRef.current) {
        setAddressError('El servicio de Google Maps no está disponible');
        return;
      }
      
      // Use Places Service to validate and geocode the address
      const request = {
        query: newStop,
        fields: ['name', 'geometry', 'formatted_address'],
        componentRestrictions: { country: 'ar' }
      };
      
placesServiceRef.current.findPlaceFromQuery(request, (results, status) => {
  if (status === google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
    const place = results[0];
    
    if (place.geometry && place.geometry.location) {
      setAddressError(null);
      
      const newStopObj: Stop = {
        id: Date.now().toString(),
        address: place.formatted_address || place.name || newStop,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        name: place.name || '',
      };
      
      setStops(prev => [...prev, newStopObj]);
      setNewStop('');
      
      // Limpiar URL si se está editando un viaje
      if (isEditing && selectedTrip) {
        setShortenedUrls(prev => {
          const updated = { ...prev };
          delete updated[selectedTrip.id];
          return updated;
        });
      }
      
      // Update map center to the new stop
      setCenter({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      });
    }
  } else {
    setAddressError('No se pudo encontrar la dirección. Intente ser más específico.');
  }
});
    }
  };
  
  // Calculate route when stops change
  useEffect(() => {
    if (!isLoaded || stops.length < 2) {
      setDirections(null);
      return;
    }
    
    const directionsService = new google.maps.DirectionsService();
    
    const origin = stops[0];
    const destination = stops[stops.length - 1];
    const waypoints = stops.slice(1, -1).map(stop => ({
      location: new google.maps.LatLng(stop.lat, stop.lng),
      stopover: true
    }));
    
    console.log(`🗺️ Calculando ruta con Google Maps...`);
    console.log(`   Origen: ${origin.address}`);
    console.log(`   Destino: ${destination.address}`);
    console.log(`   Waypoints: ${waypoints.length}`);
    
    directionsService.route(
      {
        origin: new google.maps.LatLng(origin.lat, origin.lng),
        destination: new google.maps.LatLng(destination.lat, destination.lng),
        waypoints,
        optimizeWaypoints: false, // Mantener orden original
        travelMode: google.maps.TravelMode.DRIVING,
        avoidHighways: false,
        avoidTolls: false,
        region: 'AR',
        unitSystem: google.maps.UnitSystem.METRIC, // Asegurar sistema métrico
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: google.maps.TrafficModel.BEST_GUESS
        }
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          setDirections(result);
          
          // Calcular tiempo total
          let totalTime = 0;
          result.routes[0].legs.forEach((leg) => {
            const duration = leg.duration?.value || 0;
            totalTime += duration;
          });
          
          setEstimatedTime(Math.round(totalTime / 60));
          setTrafficCondition('normal');
          
          console.log('✅ Ruta calculada exitosamente por Google Maps');
          console.log(`   Tiempo estimado: ${Math.round(totalTime / 60)} minutos`);
          
          // Log detallado de cada tramo
          result.routes[0].legs.forEach((leg, index) => {
            console.log(`   Tramo ${index + 1}: ${leg.distance?.text} - ${leg.duration?.text}`);
          });
        } else {
          console.error(`❌ Error en Google Maps: ${status}`);
          setDirections(null);
          setEstimatedTime(0);
          setTrafficCondition('normal');
        }
      }
    );
  }, [stops, isLoaded]);

  // Actualizar tráfico cada 5 minutos
useEffect(() => {
  if (stops.length < 2) return;
  
  const interval = setInterval(() => {
    console.log('Actualizando información de tráfico...');
    // Esto disparará el useEffect de la ruta
    setStops(prev => [...prev]);
  }, 5 * 60 * 1000); // 5 minutos
  
  return () => clearInterval(interval);
}, [stops.length]);

// Calculate total distance and cost
  const calculateTripDetails = useCallback(() => {
    if (!directions?.routes?.[0]) {
      console.log('⚠️ No hay ruta calculada por Google Maps');
      return { totalDistance: 0, totalCost: 0 };
    }
    
    const route = directions.routes[0];
    
    // Verificar que todas las legs tienen distancia válida
    const allLegsHaveDistance = route.legs.every(leg => leg.distance?.value !== undefined);
    
    if (!allLegsHaveDistance) {
      console.warn('⚠️ Algunas rutas no tienen distancia calculada por Google Maps');
      return { totalDistance: 0, totalCost: 0 };
    }
    
    // Usar EXACTAMENTE la distancia que Google Maps calculó
    let totalDistanceMeters = 0;
    let totalDistanceText = '';
    
    route.legs.forEach((leg, index) => {
      const legDistance = leg.distance?.value || 0;
      totalDistanceMeters += legDistance;
      totalDistanceText += `${leg.distance?.text || '0 km'}`;
      if (index < route.legs.length - 1) totalDistanceText += ' + ';
      
      console.log(`📍 Tramo ${index + 1}: ${(legDistance / 1000).toFixed(3)} km (${leg.distance?.text})`);
    });
    
    const totalDistanceKm = totalDistanceMeters / 1000;
    const totalCost = totalDistanceKm * costPerKm;
    
    console.log(`📍 TOTAL Google Maps: ${totalDistanceKm.toFixed(2)} km`);
    console.log(`📍 Distancia mostrada en Maps: ${totalDistanceText}`);
    console.log(`💰 Costo calculado: ${formatCurrency(totalCost)} (${costPerKm} por km)`);
    
    return { 
      totalDistance: Math.round(totalDistanceKm * 100) / 100, // Redondear a 2 decimales
      totalCost: Math.round(totalCost * 100) / 100 // Redondear a 2 decimales
    };
  }, [directions, costPerKm]);

  // Función para validar que la distancia coincida exactamente con Google Maps
  const validateDistanceWithGoogleMaps = useCallback((calculatedDistance: number) => {
    if (!directions?.routes?.[0]) {
      console.log('⚠️ No hay ruta de Google Maps para validar');
      return true;
    }
    
    const route = directions.routes[0];
    const googleMapsTotal = route.legs.reduce((total, leg) => {
      return total + ((leg.distance?.value || 0) / 1000);
    }, 0);
    
    const difference = Math.abs(calculatedDistance - googleMapsTotal);
    const percentageDiff = (difference / googleMapsTotal) * 100;
    
    console.log(`🔍 Validación de distancia:`);
    console.log(`   Calculado: ${calculatedDistance.toFixed(3)} km`);
    console.log(`   Google Maps: ${googleMapsTotal.toFixed(3)} km`);
    console.log(`   Diferencia: ${difference.toFixed(3)} km (${percentageDiff.toFixed(2)}%)`);
    
    // Ser más estricto: tolerancia de 0.1% en lugar de 1%
    if (percentageDiff > 0.1) {
      console.error(`❌ Diferencia significativa detectada:`);
      console.error(`   Calculado: ${calculatedDistance.toFixed(3)} km`);
      console.error(`   Google Maps: ${googleMapsTotal.toFixed(3)} km`);
      console.error(`   Diferencia: ${difference.toFixed(3)} km (${percentageDiff.toFixed(2)}%)`);
      return false;
    }
    
    console.log(`✅ Distancia validada: ${calculatedDistance.toFixed(3)} km`);
    return true;
  }, [directions]);
  
  const { totalDistance, totalCost } = calculateTripDetails();
  
  // Save trip to database
  const saveTrip = async () => {
    if (stops.length < 2) {
      toast.error('Error', 'Se necesitan al menos 2 paradas para guardar un viaje');
      return;
    }
    
    if (!tripName) {
      toast.error('Error', 'El nombre del viaje es obligatorio');
      return;
    }
    
    if (!selectedDriver) {
      toast.error('Error', 'Debe seleccionar un transportista');
      return;
    }
    
    try {
      setIsLoading(true);
      
// Validar distancia antes de guardar
if (!validateDistanceWithGoogleMaps(totalDistance)) {
  toast.error('Error de validación', 'La distancia calculada no coincide con Google Maps. Intente recalcular la ruta.');
  return;
}

const tripData = {
  name: tripName,
  driver_id: selectedDriver,
  date: tripDate,
  cost_per_km: costPerKm,
  total_distance: totalDistance,
  total_cost: totalCost,
  stops: JSON.stringify(stops),
  status: 'in_progress',
};
      
      if (isEditing && selectedTrip) {
        // Update existing trip
        const { error } = await supabase
          .from('logistics_trips')
          .update(tripData)
          .eq('id', selectedTrip.id);
        
        if (error) throw error;
        
        toast.success('Viaje actualizado', 'El viaje ha sido actualizado correctamente');
      } else {
        // Create new trip
        const { data, error } = await supabase
          .from('logistics_trips')
          .insert([tripData])
          .select();
        
        if (error) throw error;
        
        toast.success('Viaje guardado', 'El viaje ha sido guardado correctamente');
      }
      
      // Refresh trips
      const { data, error } = await supabase
        .from('logistics_trips')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Parse stops JSON for each trip
      const parsedTrips = data?.map(trip => ({
        ...trip,
        stops: typeof trip.stops === 'string' ? JSON.parse(trip.stops) : trip.stops
      })) || [];
      
      setTrips(parsedTrips);
      
      // Reset form
      resetForm();
    } catch (error: any) {
      console.error('Error saving trip:', error);
      toast.error('Error', error.message || 'Error al guardar el viaje');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load trip for editing
// Load trip for editing
const loadTrip = (trip: Trip) => {
  setSelectedTrip(trip);
  setIsEditing(true);
  setTripName(trip.name);
  setSelectedDriver(trip.driver_id);
  setTripDate(trip.date);
  setCostPerKm(trip.cost_per_km);
  setStops(trip.stops);
  
  // Limpiar URL corta cuando se edita un viaje
  setShortenedUrls(prev => {
    const updated = { ...prev };
    delete updated[trip.id];
    return updated;
  });
  
  // Center map on first stop
  if (trip.stops.length > 0) {
    setCenter({
      lat: trip.stops[0].lat,
      lng: trip.stops[0].lng
    });
  }
};
  
  // Reset form
  const resetForm = () => {
    setTripName('');
    setSelectedDriver('');
    setTripDate(new Date().toISOString().split('T')[0]);
    setCostPerKm(700);
    setStops([]);
    setDirections(null);
    setIsEditing(false);
    setSelectedTrip(null);
  };
  
  // Remove stop
// Remove stop
const removeStop = (id: string) => {
  setStops(prev => prev.filter(stop => stop.id !== id));
  
  // Limpiar URL si se está editando un viaje
  if (isEditing && selectedTrip) {
    setShortenedUrls(prev => {
      const updated = { ...prev };
      delete updated[selectedTrip.id];
      return updated;
    });
  }
};

// Update stop note
const updateStopNote = (id: string, notes: string) => {
  setStops(prev => prev.map(stop => 
    stop.id === id ? { ...stop, notes } : stop
  ));
  setEditingNoteId(null);
  
  // Limpiar URL si se está editando un viaje
  if (isEditing && selectedTrip) {
    setShortenedUrls(prev => {
      const updated = { ...prev };
      delete updated[selectedTrip.id];
      return updated;
    });
  }
};
  
  // Move stop up or down
  const moveStop = (id: string, direction: 'up' | 'down') => {
    const index = stops.findIndex(stop => stop.id === id);
    if (index === -1) return;
    
    const newStops = [...stops];
    
    if (direction === 'up' && index > 0) {
      [newStops[index], newStops[index - 1]] = [newStops[index - 1], newStops[index]];
    } else if (direction === 'down' && index < stops.length - 1) {
      [newStops[index], newStops[index + 1]] = [newStops[index + 1], newStops[index]];
    }
    
    setStops(newStops);
  };

  // Function to shorten URL using is.gd API
const shortenUrl = async (longUrl: string, tripId: string): Promise<string> => {
  // Check if we already have a shortened URL for this trip
  if (shortenedUrls[tripId]) {
    return shortenedUrls[tripId];
  }
  
  try {
    setIsGeneratingUrl(true);
    
    // Use is.gd API as per their documentation
    const apiUrl = `https://is.gd/create.php?format=json&url=${encodeURIComponent(longUrl)}`;
    
    const response = await fetch(apiUrl);
    
    if (response.ok) {
      const data = await response.json();
      
      if (data.shorturl) {
        // Store the shortened URL
        setShortenedUrls(prev => ({
          ...prev,
          [tripId]: data.shorturl
        }));
        
        console.log('URL shortened successfully:', data.shorturl);
        return data.shorturl;
      } else if (data.errormessage) {
        console.error('is.gd API error:', data.errormessage);
      }
    } else {
      console.error('HTTP error:', response.status, response.statusText);
    }
    
    // If shortening fails, return original URL
    return longUrl;
    
  } catch (error) {
    console.error('Error shortening URL:', error);
    return longUrl;
  } finally {
    setIsGeneratingUrl(false);
  }
};
  
const shareViaWhatsApp = async (trip: Trip) => {
  const driverName = drivers.find(d => d.id === trip.driver_id)?.name || 'No asignado';
  
  // Create Google Maps URL
// Create Google Maps URL identical to what the system uses
const createGoogleMapsUrl = (stops: Stop[]) => {
  if (stops.length < 2) return '';
  
  // Usar exactamente el mismo formato que el Directions API
  const origin = encodeURIComponent(stops[0].address);
  const destination = encodeURIComponent(stops[stops.length - 1].address);
  
  let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
  
  if (stops.length > 2) {
    const waypoints = stops.slice(1, -1)
      .map(stop => encodeURIComponent(stop.address))
      .join('|');
    url += `&waypoints=${waypoints}`;
  }
  
  // Agregar parámetros para que coincida con el sistema
  url += '&region=AR';
  
  return url;
};
  const longUrl = createGoogleMapsUrl(trip.stops);
  console.log('Original URL:', longUrl);
  
  // Show loading
  toast.info('Generando enlace corto...', 'Esto puede tomar unos segundos');
  
  // Get shortened URL
  const finalUrl = await shortenUrl(longUrl, trip.id);
  
  // Build message
  let message = `🚚 ${trip.total_distance.toFixed(1)} km - ${formatCurrency(trip.total_cost)}\n\n`;
  
  message += `*RECORRIDO:*\n`;
  trip.stops.forEach((stop, index) => {
    const emoji = index === 0 ? '📍' : index === trip.stops.length - 1 ? '📍' : '📍';
    message += `${emoji} ${stop.address}\n`;
    if (stop.notes) {
      message += `   (${stop.notes})\n`;
    }
  });
  
  message += `\n🗺️ Ver ruta: ${finalUrl}`;
  
  console.log('Final message:', message);

  navigator.clipboard.writeText(message).then(() => {
    if (finalUrl !== longUrl && finalUrl.includes('is.gd')) {
      toast.success('Recorrido copiado al portapapeles', 'Ya lo podés enviar al transportisa');
    } else {
      toast.success('¡Copiado!', 'Mensaje copiado (URL original)');
    }
  }).catch(() => {
    toast.error('Error', 'No se pudo copiar al portapapeles');
  });
};
  
  // Filter trips
  const filteredTrips = trips.filter(trip => {
    // Filter by driver
    if (driverFilter !== 'all' && trip.driver_id !== driverFilter) {
      return false;
    }
    
    // Filter by status
    if (statusFilter !== 'all' && trip.status !== statusFilter) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const driverName = drivers.find(d => d.id === trip.driver_id)?.name.toLowerCase() || '';
      
      if (!trip.name.toLowerCase().includes(query) && 
          !driverName.includes(query) &&
          !trip.stops.some(stop => 
            (stop.name?.toLowerCase().includes(query) || 
             stop.address.toLowerCase().includes(query))
          )) {
        return false;
      }
    }
    
    return true;
  });
  
  // Get driver name by ID
  const getDriverName = (driverId: string) => {
    return drivers.find(d => d.id === driverId)?.name || 'No asignado';
  };
  
  // Get status display name
  const getStatusDisplay = (status: Trip['status']) => {
    switch (status) {
      case 'in_progress':
        return 'En progreso';
      case 'completed':
        return 'Completado';
      default:
        return status;
    }
  };
  
  // Get status color class
  const getStatusColorClass = (status: Trip['status']) => {
    switch (status) {
      case 'planned':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'in_progress':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };
  
  // Update trip status
  const updateTripStatus = async (tripId: string, status: Trip['status']) => {
    try {
      const { error } = await supabase
        .from('logistics_trips')
        .update({ status })
        .eq('id', tripId);
      
      if (error) throw error;
      
      // Update local state
      setTrips(prev => prev.map(trip => 
        trip.id === tripId ? { ...trip, status } : trip
      ));
      
      toast.success('Estado actualizado', `El viaje ha sido marcado como ${getStatusDisplay(status)}`);
    } catch (error: any) {
      console.error('Error updating trip status:', error);
      toast.error('Error', error.message || 'Error al actualizar el estado del viaje');
    }
  };
  
  // Delete trip
  const deleteTrip = async (tripId: string) => {
    try {
      const { error } = await supabase
        .from('logistics_trips')
        .delete()
        .eq('id', tripId);
      
      if (error) throw error;
      
      // Update local state
      setTrips(prev => prev.filter(trip => trip.id !== tripId));
      
      toast.success('Viaje eliminado', 'El viaje ha sido eliminado correctamente');
    } catch (error: any) {
      console.error('Error deleting trip:', error);
      toast.error('Error', error.message || 'Error al eliminar el viaje');
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setDriverFilter('all');
    setStatusFilter('all');
    setDateFilter('all');
    setSearchQuery('');
  };
  
  if (loadError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error al cargar Google Maps</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Verifique su conexión a internet y la clave de API de Google Maps.
          </p>
        </div>
      </div>
    );
  }
  
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center">
          <Truck className="mr-2 h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          Logística
        </h2>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={resetForm}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={saveTrip}
                disabled={isLoading}
              >
                Actualizar Viaje
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              onClick={saveTrip}
              disabled={isLoading || stops.length < 2 || !tripName || !selectedDriver}
            >
              Guardar Viaje
            </Button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map and Route Planning */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader title="Mapa y Planificación de Ruta" />
            <CardContent>
              <div className="h-[500px] w-full rounded-lg overflow-hidden mb-4">
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={center}
                  zoom={12}
                  onLoad={(mapInstance) => {
  setMap(mapInstance);
  // Inicializar el servicio de Places cuando el mapa se carga
  if (!placesServiceRef.current) {
    placesServiceRef.current = new google.maps.places.PlacesService(mapInstance);
  }
}}
                >
                  {stops.map((stop, index) => (
                    <Marker
                      key={stop.id}
                      position={{ lat: stop.lat, lng: stop.lng }}
                      label={{ text: (index + 1).toString(), color: 'white' }}
                      onClick={() => setSelectedMarker(stop)}
                    />
                  ))}
                  
                  {selectedMarker && (
                    <InfoWindow
                      position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                      onCloseClick={() => setSelectedMarker(null)}
                    >
                      <div className="p-2">
                        <h3 className="font-medium">{selectedMarker.name || 'Parada'}</h3>
                        <p className="text-sm">{selectedMarker.address}</p>
                        {selectedMarker.notes && (
                          <p className="text-xs mt-1 text-gray-600">{selectedMarker.notes}</p>
                        )}
                      </div>
                    </InfoWindow>
                  )}
                  
                  {directions && (
                    <DirectionsRenderer
                      directions={directions}
                      options={{
                        suppressMarkers: true,
                        polylineOptions: {
                          strokeColor: '#4f46e5',
                          strokeWeight: 5,
                        },
                      }}
                    />
                  )}
                </GoogleMap>
              </div>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1">
<div className="space-y-1">
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
    Agregar parada
  </label>
  <input
    ref={inputRef}
    type="text"
    value={newStop}
    onChange={(e) => setNewStop(e.target.value)}
    onKeyDown={handleAddressKeyDown}
    placeholder="Buscar dirección..."
    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
  />
  {addressError && (
    <p className="text-red-600 text-xs mt-1">{addressError}</p>
  )}
</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
  Paradas ({stops.length}) - Arrastra para reordenar
</h3>
                  
                  {stops.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
                      Agregue paradas para planificar la ruta
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {stops.map((stop, index) => (
  <div 
    key={stop.id} 
    draggable={true}
    onDragStart={(e) => {
      e.dataTransfer.setData('text/plain', index.toString());
      e.currentTarget.style.opacity = '0.5';
    }}
    onDragEnd={(e) => {
      e.currentTarget.style.opacity = '1';
    }}
    onDragOver={(e) => {
      e.preventDefault();
    }}
    onDrop={(e) => {
      e.preventDefault();
      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
      const dropIndex = index;
      
      if (dragIndex !== dropIndex) {
        const newStops = [...stops];
        const draggedStop = newStops[dragIndex];
        newStops.splice(dragIndex, 1);
        newStops.splice(dropIndex, 0, draggedStop);
        setStops(newStops);
      }
    }}
    className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 cursor-move hover:shadow-md transition-shadow"
  >
                          <div className="flex-shrink-0 w-6 h-6 bg-indigo-600 dark:bg-indigo-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {index + 1}
                          </div>
<div className="flex-1 min-w-0">
  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
    {stop.name || 'Parada sin nombre'}
  </p>
  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
    {stop.address}
  </p>
  
  {/* Campo de notas */}
  <div className="mt-2">
    {editingNoteId === stop.id ? (
      <div className="flex gap-1">
        <input
          type="text"
          defaultValue={stop.notes || ''}
          placeholder="Agregar indicación para el transportista..."
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              updateStopNote(stop.id, e.currentTarget.value);
            } else if (e.key === 'Escape') {
              setEditingNoteId(null);
            }
          }}
          onBlur={(e) => updateStopNote(stop.id, e.target.value)}
          autoFocus
        />
      </div>
    ) : (
      <div 
        className="text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
        onClick={() => setEditingNoteId(stop.id)}
      >
        {stop.notes ? `📝 ${stop.notes}` : '+ Agregar indicación'}
      </div>
    )}
  </div>
</div>
                          <div className="flex-shrink-0 flex gap-1">
                            <button
                              type="button"
                              onClick={() => moveStop(stop.id, 'up')}
                              disabled={index === 0}
                              className={`p-1 rounded-full ${
                                index === 0 
                                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                                  : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <ArrowUpDown className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeStop(stop.id)}
                              className="p-1 rounded-full text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader title="Detalles del Viaje" />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Input
                  label="Nombre del Viaje"
                  id="tripName"
                  name="tripName"
                  value={tripName}
                  onChange={(e) => setTripName(e.target.value)}
                  required
                />
                
                <Select
                  label="Transportista"
                  value={selectedDriver}
                  onChange={setSelectedDriver}
                  options={[
                    { value: '', label: 'Seleccionar transportista...' },
                    ...drivers.map(driver => ({
                      value: driver.id,
                      label: driver.name
                    }))
                  ]}
                  required
                />
                
                <Input
                  type="date"
                  label="Fecha"
                  id="tripDate"
                  name="tripDate"
                  value={tripDate}
                  onChange={(e) => setTripDate(e.target.value)}
                  required
                />
                
                <Input
                  type="number"
                  label="Costo por Km ($)"
                  id="costPerKm"
                  name="costPerKm"
                  value={costPerKm.toString()}
                  onChange={(e) => setCostPerKm(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
    <div className="flex items-center mb-2">
      <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Tiempo Estimado</h3>
    </div>
    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
      {estimatedTime > 0 ? `${Math.floor(estimatedTime / 60)}h ${estimatedTime % 60}m` : '0m'}
    </p>
    <p className={`text-xs mt-1 ${
      trafficCondition === 'pesado' ? 'text-red-600' :
      trafficCondition === 'moderado' ? 'text-yellow-600' :
      trafficCondition === 'ligero' ? 'text-orange-600' :
      'text-green-600'
    }`}>
      Estimado con {trafficCondition}
    </p>
  </div>
  
  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
    <div className="flex items-center mb-2">
      <MapPin className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Distancia Total</h3>
    </div>
    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
      {totalDistance.toFixed(2)} km
    </p>
  </div>
  
  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
    <div className="flex items-center mb-2">
      <DollarSign className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-2" />
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Costo Total</h3>
    </div>
    <p className="text-2xl font-semibold text-gray-900 dark:text-white">
      {formatCurrency(totalCost)}
    </p>
  </div>
</div>
            </CardContent>
          </Card>
        </div>
        
        {/* Trip History */}
        <div className="lg:col-span-1">
          <Card className="h-full">
            <CardHeader title="Historial de Viajes" />
            <CardContent>
              <div className="space-y-4">
                {/* Filters */}
                <div className="space-y-2">
                  <Select
                    label="Transportista"
                    value={driverFilter}
                    onChange={setDriverFilter}
                    options={[
                      { value: 'all', label: 'Todos los transportistas' },
                      ...drivers.map(driver => ({
                        value: driver.id,
                        label: driver.name
                      }))
                    ]}
                  />
                  
                  <Select
                    label="Estado"
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: 'all', label: 'Todos los estados' },
                      { value: 'in_progress', label: 'En progreso' },
                      { value: 'completed', label: 'Completado' },
                    ]}
                  />
                  
                  <div className="relative">
                    <Input
                      label="Buscar"
                      id="searchQuery"
                      name="searchQuery"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Buscar..."
                    />
                    <div className="absolute right-3 top-9">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Filter className="h-4 w-4" />}
                      onClick={resetFilters}
                    >
                      Limpiar Filtros
                    </Button>
                  </div>
                </div>
                
                {/* Trip List */}
                <div className="space-y-3 mt-4 max-h-[600px] overflow-y-auto pr-2">
                  {isLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                    </div>
                  ) : filteredTrips.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No hay viajes que coincidan con los filtros seleccionados
                    </div>
                  ) : (
                    filteredTrips.map(trip => (
                      <div 
                        key={trip.id} 
                        className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-base font-medium text-gray-900 dark:text-white">
                            {trip.name}
                          </h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColorClass(trip.status)}`}>
                            {getStatusDisplay(trip.status)}
                          </span>
                        </div>
                        
                        <div className="space-y-1 mb-3">
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <User className="h-4 w-4 mr-1" />
                            {getDriverName(trip.driver_id)}
                          </div>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatArgentinaDate(trip.date)}
                          </div>
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <MapPin className="h-4 w-4 mr-1" />
                            {trip.stops.length} paradas ({trip.total_distance.toFixed(2)} km)
                          </div>
                          <div className="flex items-center text-sm font-medium text-gray-900 dark:text-white">
                            <DollarSign className="h-4 w-4 mr-1" />
                            {formatCurrency(trip.total_cost)}
                          </div>
                        </div>
<div className="flex flex-wrap gap-2">
  <Button
    variant="outline"
    size="sm"
    icon={<Share2 className="h-4 w-4" />}
    onClick={() => shareViaWhatsApp(trip)}
  >
    Compartir
  </Button>
  
  {trip.status === 'in_progress' && (
    <>
      <Button
        variant="outline"
        size="sm"
        icon={<Edit className="h-4 w-4" />}
        onClick={() => loadTrip(trip)}
      >
        Editar
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        icon={<CheckSquare className="h-4 w-4" />}
        onClick={() => updateTripStatus(trip.id, 'completed')}
      >
        Completar
      </Button>
    </>
  )}
  
  <Button
    variant="outline"
    size="sm"
    icon={<Trash2 className="h-4 w-4" />}
    onClick={() => deleteTrip(trip.id)}
  >
    Eliminar
  </Button>
</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
};

export default Logistics;